import React from "react"

import { Truck, User } from "lucide-react"

import { cn } from "@/lib/utils"

/* Never put two growable strings (route codes, driver names) on one row; fixed widths so every
   card matches. */

const CARD_WIDTH = {
  sm: "w-[248px]",
  md: "w-[288px]",
} as const

export function MapCard({
  size = "sm",
  className,
  children,
}: {
  size?: keyof typeof CARD_WIDTH
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border border-palette-jungleGreen200 bg-white shadow-lg",
        CARD_WIDTH[size],
        className,
      )}
    >
      {children}
    </div>
  )
}

export function LocationIdentity({
  name,
  address,
  size = "sm",
}: {
  name: string
  address?: string
  size?: "sm" | "md"
}) {
  return (
    <div className="min-w-0">
      <div
        className={cn(
          "line-clamp-2 break-words font-semibold text-palette-jungleGreen900",
          size === "md" ? "text-sm" : "text-xs",
        )}
        title={name}
      >
        {name}
      </div>
      {address && (
        <div
          className={cn(
            "mt-0.5 line-clamp-2 break-words text-palette-jungleGreen500",
            size === "md" ? "text-xs" : "text-[10px]",
          )}
          title={address}
        >
          {address}
        </div>
      )}
    </div>
  )
}

export function RouteIdentity({
  code,
  driverName,
  size = "sm",
  codeClassName,
}: {
  code: string
  driverName?: string | null
  size?: "sm" | "md"
  codeClassName?: string
}) {
  return (
    <div className="min-w-0">
      <div className="flex min-w-0 items-start gap-1.5">
        <Truck
          size={size === "md" ? 14 : 12}
          className={cn("flex-shrink-0 text-amber-600", size === "md" ? "mt-0.5" : "mt-px")}
        />
        <span
          className={cn(
            "line-clamp-2 min-w-0 break-words font-semibold text-amber-700",
            size === "md" ? "text-[13px] leading-snug" : "text-xs leading-snug",
            codeClassName,
          )}
          title={code}
        >
          {code}
        </span>
      </div>
      {/* An unassigned route reads as a bug if the row just vanishes, so name the gap. */}
      <div
        className={cn(
          "mt-1 flex min-w-0 items-center gap-1.5 text-palette-jungleGreen600",
          size === "md" ? "text-xs" : "text-[10px]",
        )}
      >
        <User
          size={size === "md" ? 12 : 10}
          className="flex-shrink-0 text-palette-jungleGreen400"
        />
        <span
          className={cn("truncate", !driverName && "italic text-palette-jungleGreen400")}
          title={driverName ?? undefined}
        >
          {driverName || "Unassigned"}
        </span>
      </div>
    </div>
  )
}

export function MapCardLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-medium uppercase tracking-wide text-palette-jungleGreen500">
      {children}
    </div>
  )
}
